/**
 * Permissions Management Page
 *
 * Displays and manages permission matrix for all user roles
 */

import PageHeader from '@/components/common/PageHeader';
import { CreateRoleModal, EditRoleModal } from '@/features/settings/components';
import { PermissionsMatrixAntd } from '@/features/settings/components/PermissionsMatrixAntd';
import { PermissionCategories } from '@/features/settings/constants/permissions';
import { useAppRoleList, useCreateAppRole, useDeleteAppRole, useUpdateAppRole } from '@/features/users/hooks';
import {
  IAppRole
} from "@/types";
import { InternalPermissionState, Permission } from '@/types/permissions.types';
import { DownloadOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Modal, Select, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useGetCurrentUser } from '@/hooks/app';
import { PermissionChecker } from '@/utils/permission';

export const Permissions = () => {
  // Get current user permissions
  const { data: currentUserData } = useGetCurrentUser();
  const permissions = currentUserData?.permission;

  // Initialize PermissionChecker
  const permissionChecker = new PermissionChecker(permissions);

  // Permission checks
  const canAddRoles = permissionChecker.canAddRoles();
  const canUpdateRoles = permissionChecker.canUpdateRoles();
  const canAssignPermissionToRole = permissionChecker.canAssignPermissionToRole();
  const canViewPermissions = permissionChecker.canViewPermissions();

  // Fetch app roles from API
  const { data: appRolesData, isLoading: isLoadingRoles, refetch: refetchRoles } = useAppRoleList({
    queryParams: { current: 0, pageSize: 100,filter:"&$filter=dogma_defaultsecurityrole eq true" },
    enabled: true,
  });

  const [permissionState, setPermissionState] = useState<InternalPermissionState>({});
  const [customRoles, setCustomRoles] = useState<IAppRole[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [createRoleModalOpen, setCreateRoleModalOpen] = useState(false);
  const [editRoleModalOpen, setEditRoleModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<IAppRole | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Mutations
  const createRoleMutation = useCreateAppRole();
  const updateRoleMutation = useUpdateAppRole();
  const deleteRoleMutation = useDeleteAppRole();

  // Initialize permission state from API roles only
  useEffect(() => {
    const initializePermissionState = (): InternalPermissionState => {
      const state: InternalPermissionState = {};

      // Initialize custom roles from API only
      if (appRolesData?.value) {
        const customRolesList: IAppRole[] = [];

        appRolesData.value.forEach((role) => {
          const roleKey = role.dogma_approleid;
          state[roleKey] = {};

          // Parse permissions JSON - NEW FLAT STRUCTURE: { "permission": true }
          let rolePermissions: Record<string, boolean> = {};
          if (role.dogma_permissionsjson) {
            try {
              const parsed = JSON.parse(role.dogma_permissionsjson);
              // New flat structure: { "view_dashboard": true, "view_quotes": false, ... }
              rolePermissions = parsed;
            } catch (error) {
            }
          }

          // Set permissions based on parsed JSON
          // Get all available permissions from categories
          PermissionCategories.forEach((category) => {
            category.permissions.forEach((perm) => {
              state[roleKey]![perm.id] = {
                enabled: rolePermissions[perm.id] ?? false,
                modified: false,
              };
            });
          });

          customRolesList.push(role);
        });

        setCustomRoles(customRolesList);
      }

      return state;
    };

    if (!isLoadingRoles) {
      setPermissionState(initializePermissionState());
    }
  }, [appRolesData, isLoadingRoles]);

  // Handle permission toggle
  const handlePermissionToggle = (role: string, permission: Permission) => {
    setPermissionState((prev) => {
      const newState = { ...prev };

      if (!newState[role]) {
        newState[role] = {};
      }

      const currentEnabled = newState[role]![permission]?.enabled ?? true;

      newState[role]![permission] = {
        enabled: !currentEnabled,
        modified: true,
      };

      setHasChanges(true);
      return newState;
    });
  };

  // Reset to original state
  const handleReset = () => {
    Modal.confirm({
      title: 'Reset Permissions',
      content: 'Are you sure you want to reset all changes? This will restore permissions from the API.',
      onOk: () => {
        refetchRoles(); // Refetch from API
        setHasChanges(false);

      },
    });
  };

  // Save changes
  const handleSave = async () => {
    Modal.confirm({
      title: 'Save Permission Changes',
      content: 'Are you sure you want to save these permission changes? This will update custom roles in the system.',
      onOk: async () => {
        try {
          // Save only custom roles (not default UserRole enums)
          const savePromises = customRoles.map((role) => {
            const roleKey = role.dogma_approleid;
            const rolePermissions = permissionState[roleKey];

            if (!rolePermissions) return Promise.resolve();

            // Check if this role has modifications
            const hasModifications = Object.values(rolePermissions).some((p) => p.modified);
            if (!hasModifications) return Promise.resolve();

            // Generate permissions JSON - NEW FLAT STRUCTURE: { "permission": true }
            const permissionsJson: Record<string, boolean> = {};
            Object.entries(rolePermissions).forEach(([permission, state]) => {
              permissionsJson[permission] = state.enabled;
            });

            // Update the role
            return updateRoleMutation.mutateAsync({
              id: role.dogma_approleid,
              payload: {
                dogma_name: role.dogma_name,
                dogma_permissionsjson: JSON.stringify(permissionsJson),
                dogma_defaultsecurityrole: role.dogma_defaultsecurityrole || false,
              },
            });
          });

          await Promise.all(savePromises);

          // Mark all as unmodified
          setPermissionState((prev) => {
            const newState = { ...prev };
            Object.keys(newState).forEach((role) => {
              const rolePerms = newState[role];
              if (rolePerms) {
                Object.keys(rolePerms).forEach((permission) => {
                  rolePerms[permission]!.modified = false;
                });
              }
            });
            return newState;
          });

          setHasChanges(false);

          refetchRoles();
        } catch (error: any) {

        }
      },
    });
  };

  // Export to JSON
  const handleExportJSON = () => {
    const jsonData = JSON.stringify(permissionState, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `permissions-state-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);

  };

  // Handle create role
  const handleCreateRole = (data: {
    roleName: string;
    isDefaultRole: boolean;
    permissions: Permission[];
    permissionsJson: string;
  }) => {
    createRoleMutation.mutate(
      {
        dogma_name: data.roleName,
        dogma_defaultsecurityrole: data.isDefaultRole,
        dogma_permissionsjson: data.permissionsJson,
        statecode: 0,
        statuscode: 1,
      },
      {
        onSuccess: () => {

          setCreateRoleModalOpen(false);
          refetchRoles();
        },
      }
    );
  };

  // Handle edit role click
  const handleEditRoleClick = (role: IAppRole) => {
    setSelectedRole(role);
    setEditRoleModalOpen(true);
  };

  // Handle update role
  const handleUpdateRole = (data: {
    roleId: string;
    roleName: string;
    permissions: Permission[];
    permissionsJson: string;
  }) => {
    updateRoleMutation.mutate(
      {
        id: data.roleId,
        payload: {
          dogma_name: data.roleName,
          dogma_permissionsjson: data.permissionsJson,
          dogma_defaultsecurityrole: true,
        },
      },
      {
        onSuccess: () => {

          setEditRoleModalOpen(false);
          setSelectedRole(null);
          refetchRoles();
        },

      }
    );
  };

  // Handle delete role
  const handleDeleteRole = (roleId: string) => {
    deleteRoleMutation.mutate(roleId, {
      onSuccess: () => {

        setEditRoleModalOpen(false);
        setSelectedRole(null);
        refetchRoles();
      },

    });
  };

  return (
    <div className="p-6">
      <PageHeader
        title="Permissions Management"
        subtitle="View and manage role-based permissions across all user roles"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Settings', path: '/settings' },
          { label: 'Permissions' },
        ]}
        actions={
          <>
            <Select
              value={selectedCategory}
              onChange={setSelectedCategory}
              style={{ minWidth: 200 }}
              options={[
                { value: 'all', label: '📋 All Categories' },
                ...PermissionCategories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
            />
            {canAddRoles && (
              <Button
                icon={<PlusOutlined />}
                onClick={() => setCreateRoleModalOpen(true)}
                type="default"
              >
                Add Role
              </Button>
            )}
            {canViewPermissions && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportJSON}
              >
                Export JSON
              </Button>
            )}
            {canAssignPermissionToRole && (
              <>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={!hasChanges}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  disabled={!hasChanges}
                >
                  Save Changes
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="mt-6">
        {isLoadingRoles ? (
          <div className="flex justify-center items-center py-20">
            <Spin size="large">
              <div style={{ padding: '50px' }} />
            </Spin>
          </div>
        ) : (
          <PermissionsMatrixAntd
            editable={canAssignPermissionToRole}
            showHeader={false}
            permissionState={permissionState}
            customRoles={customRoles}
            selectedCategory={selectedCategory}
            onPermissionToggle={handlePermissionToggle}
            onRoleHeaderClick={canUpdateRoles ? handleEditRoleClick : undefined}
          />
        )}
      </div>

      <CreateRoleModal
        open={createRoleModalOpen}
        loading={createRoleMutation.isPending}
        onCancel={() => setCreateRoleModalOpen(false)}
        onSubmit={handleCreateRole}
      />

      <EditRoleModal
        open={editRoleModalOpen}
        role={selectedRole}
        permissionState={permissionState}
        loading={updateRoleMutation.isPending}
        deleteLoading={deleteRoleMutation.isPending}
        onCancel={() => {
          setEditRoleModalOpen(false);
          setSelectedRole(null);
        }}
        onUpdate={handleUpdateRole}
        onDelete={handleDeleteRole}
      />
    </div>
  );
};

export default Permissions;
